import { DOCUMENT } from '@angular/common';
import { inject, Injectable, InjectionToken, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Meta, Title } from '@angular/platform-browser';

/**
 * Origine publique du site, sans « / » final (ex. https://lyricstream.app).
 * Navigateur : `location.origin`. Serveur SSR : fournie par `app.config.server.ts` depuis `SITE_URL`
 * (même variable que `seo.ts` pour le sitemap). Vide = URL absolues impossibles → canonical / og:url omis.
 */
export const SITE_ORIGIN = new InjectionToken<string>('SITE_ORIGIN', {
  providedIn: 'root',
  factory: () => {
    const platformId = inject(PLATFORM_ID);
    return isPlatformBrowser(platformId) ? inject(DOCUMENT).location.origin : '';
  },
});

export interface SeoAlternate {
  /** Code hreflang : `fr`, `ht`, `en`, `es` ou `x-default`. */
  lang: string;
  /** Chemin du site, commençant par « / ». */
  path: string;
}

export interface SeoData {
  /** Titre complet de l'onglet / de l'aperçu (ex. « Mon chant — LyricStream »). */
  title: string;
  description?: string;
  /** Chemin canonique du site (« /fr/lyric/slug »), ou URL absolue. */
  path?: string;
  /** Image d'aperçu : URL absolue, ou chemin du site (« /assets/… »). */
  image?: string;
  /** `og:type` (défaut `website`). */
  type?: 'website' | 'article' | 'music.song' | 'profile';
  /** Langue de l'interface (`fr`…), pour `og:locale`. */
  lang?: string;
  /** Alternates hreflang — pages d'INTERFACE uniquement, jamais les chants ni les recueils. */
  alternates?: SeoAlternate[];
  /** Page à ne pas indexer (ex. chant introuvable). Ne pas utiliser pour QA : nginx s'en charge. */
  noindex?: boolean;
}

const SITE_NAME = 'LyricStream';
const OG_LOCALES: Record<string, string> = { fr: 'fr_FR', ht: 'ht_HT', en: 'en_US', es: 'es_ES' };

/** Balises <meta> gérées par le service : toutes sont retirées puis reposées à chaque `set()`. */
const MANAGED_META = [
  'name="description"',
  'name="robots"',
  'property="og:title"',
  'property="og:description"',
  'property="og:type"',
  'property="og:url"',
  'property="og:image"',
  'property="og:site_name"',
  'property="og:locale"',
  'name="twitter:card"',
  'name="twitter:title"',
  'name="twitter:description"',
  'name="twitter:image"',
] as const;

/**
 * Balises SEO / partage (title, description, canonical, Open Graph, Twitter, hreflang).
 * Compatible SSR : n'utilise que `Title`, `Meta` et `DOCUMENT` (pas de `window`).
 * Chaque `set()` REMPLACE l'ensemble des balises gérées : rien ne survit à la navigation.
 */
@Injectable({ providedIn: 'root' })
export class SeoService {
  private readonly title = inject(Title);
  private readonly meta = inject(Meta);
  private readonly doc = inject(DOCUMENT);
  private readonly siteOrigin = inject(SITE_ORIGIN);

  set(data: SeoData): void {
    this.clear();
    this.title.setTitle(data.title);

    const url = this.absolute(data.path);
    const image = this.absolute(data.image);

    this.tag('name="description"', data.description);
    this.tag('name="robots"', data.noindex ? 'noindex, nofollow' : undefined);

    this.tag('property="og:title"', data.title);
    this.tag('property="og:description"', data.description);
    this.tag('property="og:type"', data.type ?? 'website');
    this.tag('property="og:url"', url);
    this.tag('property="og:image"', image);
    this.tag('property="og:site_name"', SITE_NAME);
    this.tag('property="og:locale"', data.lang ? OG_LOCALES[data.lang] : undefined);

    this.tag('name="twitter:card"', image ? 'summary_large_image' : 'summary');
    this.tag('name="twitter:title"', data.title);
    this.tag('name="twitter:description"', data.description);
    this.tag('name="twitter:image"', image);

    if (url) this.link('canonical', url);
    for (const alt of data.alternates ?? []) {
      const href = this.absolute(alt.path);
      if (href) this.link('alternate', href, alt.lang);
    }
  }

  /** Retire toutes les balises gérées (title inchangé). */
  clear(): void {
    for (const selector of MANAGED_META) this.meta.removeTag(selector);
    this.doc.head.querySelectorAll('link[rel="canonical"], link[rel="alternate"][hreflang]').forEach((el) => el.remove());
  }

  /** Chemin du site (« /fr/x ») → URL absolue ; URL absolue inchangée ; sans origine connue → undefined. */
  private absolute(pathOrUrl: string | undefined): string | undefined {
    if (!pathOrUrl) return undefined;
    if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
    if (!this.siteOrigin) return undefined;
    return `${this.siteOrigin}${pathOrUrl.startsWith('/') ? '' : '/'}${pathOrUrl}`;
  }

  private tag(selector: (typeof MANAGED_META)[number], content: string | undefined): void {
    if (!content) return;
    const [attr, value] = selector.split('=');
    this.meta.addTag({ [attr]: value.replace(/"/g, ''), content });
  }

  private link(rel: string, href: string, hreflang?: string): void {
    const el = this.doc.createElement('link');
    el.setAttribute('rel', rel);
    if (hreflang) el.setAttribute('hreflang', hreflang);
    el.setAttribute('href', href);
    this.doc.head.appendChild(el);
  }
}
